import { motion } from "framer-motion";
import { Building2, Zap, Film, LayoutGrid, MapPin, Mic2, Images, Instagram, Youtube, Globe, Home, Linkedin, Grid3x3, Facebook } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 32 — One property becomes 5 distinct content types.
 * Layout: Home icon underlay top-right (no box) · 5 outcome cards centred.
 */

const outputs = [
  {
    n: "01",
    icon: Zap,
    title: "Sneak Peek",
    note: "Fast, high energy",
    hook: "\"This will go in days…\"",
    platforms: ["Reels", "TikTok"],
    accent: "#96C11F",
  },
  {
    n: "02",
    icon: Film,
    title: "Full Tour",
    note: "Long-form walk-through",
    hook: "Featured property feature",
    platforms: ["YouTube", "Website"],
    accent: "#00deb6",
  },
  {
    n: "03",
    icon: LayoutGrid,
    title: "Story Cuts",
    note: "5–10 clips · auto-cut",
    hook: "\"Kitchen → Garden → Bedroom\"",
    platforms: ["Stories"],
    accent: "#e9a820",
  },
  {
    n: "04",
    icon: Images,
    title: "Social Images",
    note: "On-brand stills · in seconds",
    hook: "\"Same property · ten posts\"",
    platforms: ["Grid", "LinkedIn", "Stories"],
    accent: "#96C11F",
  },
  {
    n: "05",
    icon: Mic2,
    title: "PresenTour / NarraTour",
    note: "Property video",
    hook: "\"Presenter-ready · on-brand\"",
    platforms: ["Website", "YouTube", "Facebook", "Instagram"],
    accent: "#e50064",
  },
];

const platformIcons = {
  Reels: Instagram,
  TikTok: null,
  YouTube: Youtube,
  Website: Globe,
  Stories: Instagram,
  Local: MapPin,
  Grid: Grid3x3,
  LinkedIn: Linkedin,
  Facebook: Facebook,
  Instagram: Instagram,
};

export default function Scene32_OnePropertyBecomes() {
  return (
    <SceneShell variant="forest" align="left" data-testid="scene-one-property-becomes">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, rgba(150,193,31,0.16) 0%, transparent 60%)",
        }}
      />

      {/* Home icon underlay — top-right (no box) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, x: 20 }}
        animate={{ opacity: 0.18, scale: 1, x: 0 }}
        transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute top-12 right-12 pointer-events-none"
        data-testid="property-home-underlay"
      >
        <Home
          className="w-[200px] h-[200px] sm:w-[280px] sm:h-[280px] lg:w-[360px] lg:h-[360px]"
          style={{
            color: "#96C11F",
            strokeWidth: 0.7,
            filter: "drop-shadow(0 0 60px rgba(150,193,31,0.45))",
          }}
        />
        <div
          className="absolute -bottom-2 right-3 text-[10px] tracking-[0.32em] uppercase"
          style={{ color: "rgba(150,193,31,0.85)", opacity: 0.85 }}
        >
          1 property
        </div>
      </motion.div>

      <div className="relative max-w-[1600px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mb-5"
        >
          Video possibilities · as this develops
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-[900px]"
          data-testid="property-becomes-title"
        >
          One property{" "}
          <span style={{ color: "#96C11F" }}>becomes…</span>
        </motion.h2>

        {/* 5 outcome cards — centred grid */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-[1500px] mx-auto">
          {outputs.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 24, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.85,
                  delay: 0.7 + i * 0.16,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -4 }}
                className="rounded-2xl glass p-5 relative overflow-hidden flex flex-col"
                data-testid={`property-output-${i}`}
              >
                <div
                  className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
                  aria-hidden="true"
                >
                  <div
                    className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-20"
                    style={{
                      background: `radial-gradient(circle, ${o.accent} 0%, transparent 70%)`,
                      filter: "blur(20px)",
                    }}
                  />
                </div>
                <div className="relative flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className="text-[10px] tracking-[0.32em] uppercase tnum"
                      style={{ color: o.accent, fontWeight: 700 }}
                    >
                      {o.n}
                    </span>
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{
                        background: `${o.accent}1a`,
                        color: o.accent,
                        border: `1px solid ${o.accent}55`,
                      }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>
                  <div
                    className="text-base font-bold leading-tight"
                    style={{ color: "#e2e8f0" }}
                  >
                    {o.title}
                  </div>
                  <div
                    className="text-xs mt-1"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {o.note}
                  </div>
                  <div
                    className="text-xs italic mt-3 leading-snug"
                    style={{ color: "rgba(255,255,255,0.85)" }}
                  >
                    {o.hook}
                  </div>
                  <div className="mt-auto pt-4 flex flex-wrap gap-1.5">
                    {o.platforms.map((p) => {
                      const PIcon = platformIcons[p];
                      return (
                        <span
                          key={p}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[0.12em] uppercase"
                          style={{
                            background: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            color: "rgba(255,255,255,0.75)",
                          }}
                        >
                          {PIcon ? <PIcon className="w-2.5 h-2.5" /> : null}
                          {p}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 2.4, duration: 0.8 }}
          className="mt-10 text-center text-[11px] tracking-[0.32em] uppercase text-white/55"
        >
          Same listing · five different audiences · multiple platforms
        </motion.div>
      </div>
    </SceneShell>
  );
}
